// src/pages/GroupList.jsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const ACADEMIC_SEMESTERS = [
    '2025/2026 zimowy',
    '2025/2026 letni',
    '2026/2027 zimowy',
    '2026/2027 letni',
];
const FIELDS_OF_STUDY = [
    'Dietetyka I stopnia',
    'Dietetyka II stopnia',
    'Fizjoterapia',
    'Kosmetologia I stopnia',
    'Kosmetologia II stopnia',
    'Pielęgniarstwo',
    'Psychologia',
    'Zdrowie publiczne',
];
const controlStyle = {
    width: '100%',
    minHeight: 40,
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    background: '#fff',
    boxSizing: 'border-box',
};

function GroupList() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const navigate = useNavigate();

    // formularz nowej grupy
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const [form, setForm] = useState({
        group_name: '',
        semester: '2025/2026 letni',
        field_of_study: 'Dietetyka I stopnia',
        study_semester: 1,
        study_mode: 'stacjonarne',
        is_active: true,
    });

    async function load() {
        try {
            setErr('');
            setLoading(true);
            const r = await fetch('/api/prowadzacy/grupy', { headers: { ...authHeader() } });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j?.blad || `Błąd (${r.status})`);
            }
            const j = await r.json();
            setGroups(Array.isArray(j) ? j : []);
        } catch (e) {
            setErr(e.message || 'Błąd pobierania grup');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    function setField(k, v) {
        setForm(prev => ({ ...prev, [k]: v }));
    }

    async function createGroup(e) {
        e?.preventDefault?.();
        setSaveMsg('');
        setSaving(true);

        // prosta walidacja
        const required = ['group_name', 'semester', 'field_of_study', 'study_semester', 'study_mode'];
        for (const k of required) {
            if (form[k] === '' || form[k] == null) {
                setSaving(false);
                setSaveMsg('Uzupełnij wszystkie pola.');
                return;
            }
        }

        try {
            const payload = {
                group_name: String(form.group_name).trim(),
                semester: String(form.semester),
                field_of_study: String(form.field_of_study).trim(),
                study_semester: Number(form.study_semester),
                study_mode: String(form.study_mode),
                is_active: !!form.is_active,
            };

            const r = await fetch('/api/prowadzacy/grupa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify(payload),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j?.blad || `Błąd (${r.status})`);

            setSaveMsg('Dodano grupę.');
            setShowForm(false);
            // wyczyść formularz
            setForm({
                group_name: '',
                semester: '2025/2026 letni',
                field_of_study: 'Dietetyka I stopnia',
                study_semester: 1,
                study_mode: 'stacjonarne',
                is_active: true,
            });

            // odśwież listę
            await load();

            // opcjonalnie: przejście do szczegółów nowej grupy
            if (j?.group_id) {
                navigate(`/prowadzacy/grupa/${j.group_id}`);
            }
        } catch (e) {
            setSaveMsg(e.message || 'Nie udało się dodać grupy');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div style={{ padding: '0 16px', marginTop: 80, display: 'grid', gap: 16 }}>
            <h2>Moje grupy</h2>

            {/* Pasek akcji */}
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="but-smal" style={{ boxShadow: 'none' }} onClick={() => setShowForm(s => !s)}>
                    {showForm ? '✖ Zamknij' : '+ Nowa grupa'}
                </button>
            </div>

            {/* Formularz nowej grupy */}
            {showForm && (
                <form
                    onSubmit={createGroup}
                    style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, background: '#fff', display: 'grid', gap: 10 }}
                >
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Dodaj grupę</div>

                    <label style={{ display: 'grid', gap: 4 }}>
                        <span>Nazwa grupy</span>
                        <input
                            value={form.group_name}
                            onChange={e => setField('group_name', e.target.value)}
                            placeholder="np. 265N"
                            style={controlStyle}
                            required
                        />
                    </label>

                    <label style={{ display: 'grid', gap: 4 }}>
                        <span>Semestr</span>
                        <select value={form.semester} onChange={e => setField('semester', e.target.value)} style={controlStyle} required>
                            {ACADEMIC_SEMESTERS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </label>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <label style={{ display: 'grid', gap: 4 }}>
                            <span>Kierunek</span>
                            <select value={form.field_of_study} onChange={e => setField('field_of_study', e.target.value)} style={controlStyle} required>
                                {FIELDS_OF_STUDY.map(field => <option key={field} value={field}>{field}</option>)}
                            </select>
                        </label>

                        <label style={{ display: 'grid', gap: 4 }}>
                            <span>Tryb</span>
                            <select value={form.study_mode} onChange={e => setField('study_mode', e.target.value)} style={controlStyle} required>
                                <option value="stacjonarne">stacjonarne</option>
                                <option value="niestacjonarne">niestacjonarne</option>
                            </select>
                        </label>
                    </div>

                    <label style={{ display: 'grid', gap: 4 }}>
                        <span>Semestr studiów (liczba)</span>
                        <input
                            type="number"
                            min="1"
                            value={form.study_semester}
                            onChange={e => setField('study_semester', Number(e.target.value))}
                            style={controlStyle}
                            required
                        />
                    </label>

                    <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            type="checkbox"
                            checked={!!form.is_active}
                            onChange={e => setField('is_active', e.target.checked)}
                        />
                        <span>Aktywna</span>
                    </label>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                        <button className="but-smal" type="submit" disabled={saving}>
                            {saving ? 'Zapisywanie…' : 'Zapisz grupę'}
                        </button>
                        {saveMsg && (
                            <span style={{ color: saveMsg.includes('Błąd') || saveMsg.includes('udało') ? '#dc2626' : '#059669' }}>
                {saveMsg}
              </span>
                        )}
                    </div>
                </form>
            )}

            {/* Lista grup */}
            {loading ? (
                <div>Ładowanie…</div>
            ) : err ? (
                <div style={{ color: 'crimson' }}>{err}</div>
            ) : (
                <table border="1" cellPadding="10" style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
                    <thead>
                    <tr>
                        <th>Nazwa</th>
                        <th>Kierunek</th>
                        <th>Semestr studiów</th>
                        <th>Semestr</th>
                        <th>Tryb</th>
                        <th>Aktywna</th>
                        <th>Akcja</th>
                    </tr>
                    </thead>
                    <tbody>
                    {groups.map(g => (
                        <tr key={g.group_id}>
                            <td>{g.group_name}</td>
                            <td>{g.field_of_study}</td>
                            <td>{g.study_semester}</td>
                            <td>{g.semester}</td>
                            <td>{g.study_mode}</td>
                            <td>{g.is_active ? '✅' : '❌'}</td>
                            <td>
                                <button
                                    className="but-smal"
                                    style={{ boxShadow: 'none' }}
                                    onClick={() => navigate(`/prowadzacy/grupa/${g.group_id}`)}
                                >
                                    Szczegóły
                                </button>
                            </td>
                        </tr>
                    ))}
                    {groups.length === 0 && (
                        <tr>
                            <td colSpan={7} style={{ padding: 12, color: '#6b7280' }}>
                                Brak grup.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default GroupList;
